import { ForwardedRef, forwardRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocalStorage } from "react-use";

import { LocationCode } from "pwapi";

import { AuthState } from "shared/reducers/authReducer";
import { SET_USER_SELECTED_LOCATION_CODE } from "shared/reducers/locationCodesReducer";

import { useAppDispatch } from "store/hooks";

const LocationCodeSelector = forwardRef(
  (
    props: JSX.IntrinsicElements["select"],
    ref: ForwardedRef<HTMLSelectElement>
  ) => {
    const dispatch = useAppDispatch();
    const locationCodes = useSelector(
      ({ auth }: { auth: AuthState }) => auth.user?.location_codes
    );

    // Load previously used locationCodeId from localStorage
    const [locationCodeId, setLocationCodeId] = useLocalStorage<
      LocationCode["id"] | undefined
    >("lastUsedLocationCodeId", locationCodes?.at(0)?.id || undefined);
    const locationCode = locationCodes?.find((l) => l.id === locationCodeId);
    if (locationCodeId && !locationCode) {
      // In the thin edge-case that a locationCodeId was stored in localstorage
      // ... that the user doesn't have access to (for example, browser sharing?)
      setLocationCodeId(locationCodes?.at(0)?.id || undefined);
    }

    useEffect(() => {
      // Keeps store state in "sync" for signature <ReactSelect /> on <CaseReport />

      dispatch({
        type: SET_USER_SELECTED_LOCATION_CODE,
        payload: locationCode,
      });
    }, [locationCodeId, locationCodes]);

    return !!locationCodes?.length ? (
      <div className="LocationCode">
        <label>
          <small>Location Code</small>
          <select
            value={locationCodeId || ""}
            onChange={(e) => {
              setLocationCodeId(
                !Number.isNaN(e.target.value)
                  ? Number(e.target.value)
                  : undefined
              );
            }}
            {...props}
            ref={ref}
          >
            {locationCodes.map((l) => (
              <option value={l.id}>{l.code}</option>
            ))}
            <option value=""></option>
          </select>
        </label>
      </div>
    ) : null;
  }
);

export default LocationCodeSelector;
